import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { BookOpen, ExternalLink, ScrollText } from "lucide-react";
import { AskAboutButton } from "./AskAboutButton";

function useInView(threshold = 0.2) {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (!element) return;
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setVisible(true); },
            { threshold }
        );
        obs.observe(element);
        return () => obs.disconnect();
    }, [element, threshold]);
    return { ref: setElement, visible };
}

export function Publications({ publications = [], language = "fr" }: { publications?: any[], language?: string }) {
    const { ref, visible } = useInView(0.05);

    // Deduplicate on title
    const uniquePublications = publications.reduce((acc: any[], current: any) => {
        const x = acc.find(item => item.title === current.title);
        if (!x) {
            return acc.concat([current]);
        } else {
            return acc;
        }
    }, []);


    if (!uniquePublications || uniquePublications.length === 0) return null;

    return (
        <section
            id="publications"
            ref={ref}
            className="relative py-24 md:py-36 overflow-hidden"
            style={{ background: "#050508" }}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={visible ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5 }}
                    className="flex items-center gap-3 mb-16"
                >
                    <span
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: "11px",
                            color: "#22d3ee",
                            letterSpacing: "0.15em",
                        }}
                    >
                        05 // {language === "en" ? "RESEARCH" : "RECHERCHE"}
                    </span>
                    <div className="flex-1 h-px" style={{ background: "rgba(34,211,238,0.15)" }} />
                </motion.div>

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={visible ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 0.1, duration: 0.6 }}
                        style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: "clamp(32px, 4vw, 52px)",
                            fontWeight: 700,
                            color: "#ffffff",
                            letterSpacing: "-0.02em",
                            lineHeight: 1.1,
                        }}
                    >
                        {language === "en" ? "Publications & " : "Publications & "}
                        <span style={{ color: "#f87171" }}>{language === "en" ? "papers" : "articles"}</span>
                    </motion.h2>
                </div>

                <div className="grid grid-cols-1 gap-6">
                    {uniquePublications.map((pub: any, i: number) => {
                        const year = pub.publication_date ? new Date(pub.publication_date).getFullYear() : (language === "en" ? "In progress" : "En cours");
                        return (
                            <motion.div
                                key={pub.id || i}
                                initial={{ opacity: 0, y: 30 }}
                                animate={visible ? { opacity: 1, y: 0 } : {}}
                                transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
                                className="group relative p-6 md:p-8 rounded-2xl flex flex-col md:flex-row gap-6 items-start md:items-center justify-between"
                                style={{
                                    background: "rgba(255,255,255,0.02)",
                                    border: "1px solid rgba(255,255,255,0.05)",
                                    transition: "all 0.3s ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                                    e.currentTarget.style.borderColor = "rgba(248,113,113,0.3)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "rgba(255,255,255,0.02)";
                                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.05)";
                                }}
                            >
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-3 mb-3">
                                        <span
                                            className="px-3 py-1 rounded-full flex items-center gap-1.5"
                                            style={{
                                                background: "rgba(248,113,113,0.1)",
                                                color: "#f87171",
                                                fontFamily: "'JetBrains Mono', monospace",
                                                fontSize: "12px",
                                            }}
                                        >
                                            <ScrollText size={14} />
                                            {year}
                                        </span>
                                        <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "14px", color: "rgba(255,255,255,0.4)" }}>
                                            {pub.venue}
                                        </span>
                                    </div>
                                    <h3
                                        style={{
                                            fontFamily: "'Space Grotesk', sans-serif",
                                            fontSize: "22px",
                                            fontWeight: 600,
                                            color: "#fff",
                                            marginBottom: "8px",
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {pub.title}
                                    </h3>
                                    <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "15px", color: "rgba(255,255,255,0.6)" }}>
                                        {pub.authors}
                                    </p>
                                </div>

                                {pub.url && (
                                    <a
                                        href={pub.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-shrink-0 w-12 h-12 rounded-full border border-white/10 flex items-center justify-center transition-all group-hover:border-[#f87171] group-hover:bg-[#f87171]/10"
                                        style={{ color: "rgba(255,255,255,0.6)" }}
                                    >
                                        <ExternalLink size={18} className="group-hover:text-[#f87171]" />
                                    </a>
                                )}

                                <AskAboutButton
                                    subject={pub.title}
                                    language={language}
                                    color="#f87171"
                                    section="publication"
                                    context={`Titre: ${pub.title}\nAuteurs: ${pub.authors || ""}\nVenue: ${pub.venue || ""}\nDate: ${pub.publication_date || ""}\nAbstract: ${pub.abstract || ""}\nStatut: ${pub.status || ""}`}
                                />
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
